import React from 'react';
import { purchaseLFQuestionsByName } from 'app/models/questions/application-purchase';
import { NoPurchasePrice } from 'app/components/StopGates/NoPurchasePrice';
import { Confirmation } from 'app/components/Confirmation';
import * as FormActions from 'app/actions/form/actions';
import { Section } from 'app/models/types';
import { FormTerms } from 'app/components/FormAgreements/FormTerms';
import { FormParagraphs } from 'app/models/paragraphs';
import { FieldNames } from 'app/models/fields/names';
import {
  hasChosenHome,
  hasAgreement,
  hasProceedsFromSellingHome,
  isHomeowner,
  isRenting,
  isRentFree,
  isEmployee,
  isSelfEmployed,
  isRetired,
  isEmploymentOther,
  hasCoBorrower,
  isCoEmployee,
  isCoSelfEmployed,
  isCoBorrowerRetired,
  isCoBorrowerEmploymentOther,
  isExcludingSsnQuestions,
  hasLoanTypeQuestion,
} from 'app/models/fields/conditionals';
import { getLongFormDeclarationQuestions } from 'app/models/questions/declarations';
import { AuthorizeSsnFullPull } from 'app/components/FormAgreements/AuthorizeSsnFullPull';
import {
  LF_PURCHASE_CONFIRMATION,
  LF_PURCHASE_DOWNPAYMENT_INFO,
  LF_PURCHASE_GET_ADDITIONAL_PROPERTIES,
  LF_PURCHASE_GET_AGREEMENT_PREQUAL,
  LF_PURCHASE_GET_AVAILABLE_FUNDS,
  LF_PURCHASE_GET_BORROWER_INCOME_EMPLOYED_OR_SELF,
  LF_PURCHASE_GET_BORROWER_INCOME_RETIRED_OR_OTHER,
  LF_PURCHASE_GET_BORROWER_INCOME_SOURCE, LF_PURCHASE_GET_BORROWER_QUESTIONS, LF_PURCHASE_GET_BORROWER_SSN,
  LF_PURCHASE_GET_CITIZEN_AND_MILITARY,
  LF_PURCHASE_GET_COBORROWER_CITIZEN_AND_MILITARY_INFORMATION,
  LF_PURCHASE_GET_COBORROWER_HOUSING_INFORMATION,
  LF_PURCHASE_GET_COBORROWER_INCOME_EMPLOYED_OR_SELF, LF_PURCHASE_GET_COBORROWER_INCOME_RETIRED_OR_OTHER,
  LF_PURCHASE_GET_COBORROWER_INCOME_SOURCE,
  LF_PURCHASE_GET_COBORROWER_PERSONAL_INFORMATION, LF_PURCHASE_GET_COBORROWER_QUESTIONS,
  LF_PURCHASE_GET_CURRENT_HOUSING_SITUATION,
  LF_PURCHASE_GET_FICO_MARRIAGE_AND_DEPENDENTS,
  LF_PURCHASE_GET_HOUSING_FREE_INFORMATION,
  LF_PURCHASE_GET_HOUSING_OWNER_INFORMATION,
  LF_PURCHASE_GET_HOUSING_RENTER_INFORMATION,
  LF_PURCHASE_GET_LOCATION,
  LF_PURCHASE_GET_PERSONAL_INFORMATION,
  LF_PURCHASE_GET_SELL_CURRENT_HOUSE,
  LF_PURCHASE_GET_LOAN_TYPE,
  LF_PURCHASE_GET_VALUE,
} from './constants';
import { BorrowerType } from '@lenderful/domain';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getApplicationPurchaseSectionById = (id: number): Section => {
  return APPLICATION_PURCHASE_SECTIONS.find((section) => section.id === id) as Section;
};

const APPLICATION_PURCHASE_SECTIONS: Section[] = [
  {
    id       : LF_PURCHASE_GET_PERSONAL_INFORMATION,
    title    : 'Enter your personal information',
    questions: purchaseLFQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
      FieldNames.homeChosenYN,
    ]),
    agreement     : <FormTerms />,
    hideBackButton: true,
  },
  {
    id       : LF_PURCHASE_GET_AGREEMENT_PREQUAL,
    questions: purchaseLFQuestionsByName([
      FieldNames.agreementYN,
      FieldNames.prequalAmountYN,
    ]),
    submitAction: FormActions.determinePrequalTransition,
  },
  {
    id       : LF_PURCHASE_GET_VALUE,
    title    : 'Tell us about your new purchase',
    questions: purchaseLFQuestionsByName([
      FieldNames.homeValue,
      FieldNames.propertyType,
      FieldNames.propertyUsage,
      FieldNames.expectedClosingDate,
      FieldNames.propertyMonthlyRentalIncome,
      FieldNames.builtYear,
    ]),
    ifNoQuestions: NoPurchasePrice,
  },
  {
    id       : LF_PURCHASE_GET_LOAN_TYPE,
    questions: purchaseLFQuestionsByName([
      FieldNames.loanType,
    ]),
    showIf : [hasLoanTypeQuestion],
  },
  {
    id       : LF_PURCHASE_GET_LOCATION,
    questions: purchaseLFQuestionsByName([
      FieldNames.propertyStreet,
      FieldNames.propertyStreet2,
      FieldNames.propertyCity,
      FieldNames.propertyState,
      FieldNames.propertyZip,
      FieldNames.yearTaxes,
      FieldNames.yearInsure,
      FieldNames.yearAssociate,
      FieldNames.escrowUsage,
    ]),
    showIf: [hasChosenHome, hasAgreement],
  },
  {
    id       : LF_PURCHASE_GET_AVAILABLE_FUNDS,
    title    : 'Available Funds',
    paragraph: FormParagraphs.availableFunds,
    questions: purchaseLFQuestionsByName([FieldNames.assets]),
  },
  {
    id        : LF_PURCHASE_DOWNPAYMENT_INFO,
    title     : 'Down Payment Funds Summary',
    questions : purchaseLFQuestionsByName([FieldNames.assetSummary]),
  },
  {
    id       : LF_PURCHASE_GET_FICO_MARRIAGE_AND_DEPENDENTS,
    questions: purchaseLFQuestionsByName([
      FieldNames.creditScore,
      FieldNames.maritalStatus,
      FieldNames.numOfDependent,
      FieldNames.dependentsAges,
    ]),
  },
  {
    id        : LF_PURCHASE_GET_CITIZEN_AND_MILITARY,
    questions : purchaseLFQuestionsByName([
      FieldNames.citizenshipType,
      FieldNames.militaryExperience,
    ]),
  },
  {
    id         : LF_PURCHASE_GET_COBORROWER_PERSONAL_INFORMATION,
    questions  : purchaseLFQuestionsByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerDOB,
      FieldNames.coBorrowerEmail,
      FieldNames.coBorrowerPhone,
      FieldNames.coBorrowerOnTitle,
    ]),
  },
  {
    id         : LF_PURCHASE_GET_COBORROWER_HOUSING_INFORMATION,
    showIf     : [hasCoBorrower],
    questions  : purchaseLFQuestionsByName([
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
      FieldNames.coBorrowerLivingRentOrOwn,
      FieldNames.coBorrowerLivingMonthlyRentAmount,
      FieldNames.coBorrowerMaritalStatus,
    ]),
  },
  {
    id        : LF_PURCHASE_GET_COBORROWER_CITIZEN_AND_MILITARY_INFORMATION,
    showIf    : [hasCoBorrower],
    questions : purchaseLFQuestionsByName([
      FieldNames.coBorrowerCitizenshipType,
      FieldNames.coBorrowerMilitaryExperience,
    ]),
  },
  {
    id       : LF_PURCHASE_GET_CURRENT_HOUSING_SITUATION,
    title    : 'What is your current housing situation?',
    questions: purchaseLFQuestionsByName([FieldNames.livingRentOrOwn]),
  },
  {
    id       : LF_PURCHASE_GET_SELL_CURRENT_HOUSE,
    questions: purchaseLFQuestionsByName([FieldNames.homeSoldYN]),
    showIf   : [(values) => !hasProceedsFromSellingHome(values) && isHomeowner(values)],
  },
  {
    id       : LF_PURCHASE_GET_HOUSING_OWNER_INFORMATION,
    title    : 'Provide your homeowner information',
    questions: purchaseLFQuestionsByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingYearTaxes,
      FieldNames.livingYearInsure,
      FieldNames.livingYearAssociate,
      FieldNames.livingOccupancyStart,
      FieldNames.livingPrevPropertyStreet,
      FieldNames.livingPrevPropertyStreet2,
      FieldNames.livingPrevPropertyCity,
      FieldNames.livingPrevPropertyState,
      FieldNames.livingPrevPropertyZip,
      FieldNames.livingPrevRentOrOwn,
      FieldNames.livingPrevOccupancyStart,
      FieldNames.isMailingDifferent,
      FieldNames.mailingStreet,
      FieldNames.mailingStreet2,
      FieldNames.mailingCity,
      FieldNames.mailingState,
      FieldNames.mailingZip,
      FieldNames.livingEscrowUsage,
    ]),
    showIf: [isHomeowner],
  },
  {
    id         : LF_PURCHASE_GET_HOUSING_RENTER_INFORMATION,
    showIf     : [isRenting],
    title      : 'Provide your renter information',
    questions  : purchaseLFQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
      FieldNames.livingOccupancyStart,
      FieldNames.livingPrevPropertyStreet,
      FieldNames.livingPrevPropertyStreet2,
      FieldNames.livingPrevPropertyCity,
      FieldNames.livingPrevPropertyState,
      FieldNames.livingPrevPropertyZip,
      FieldNames.livingPrevRentOrOwn,
      FieldNames.livingPrevOccupancyStart,
      FieldNames.isMailingDifferent,
      FieldNames.mailingStreet,
      FieldNames.mailingStreet2,
      FieldNames.mailingCity,
      FieldNames.mailingState,
      FieldNames.mailingZip,
    ]),
  },
  {
    id         : LF_PURCHASE_GET_HOUSING_FREE_INFORMATION,
    showIf     : [isRentFree],
    title      : 'Provide your housing information',
    questions  : purchaseLFQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
      FieldNames.livingPrevPropertyStreet,
      FieldNames.livingPrevPropertyStreet2,
      FieldNames.livingPrevPropertyCity,
      FieldNames.livingPrevPropertyState,
      FieldNames.livingPrevPropertyZip,
      FieldNames.livingPrevRentOrOwn,
      FieldNames.livingPrevOccupancyStart,
      FieldNames.isMailingDifferent,
      FieldNames.mailingStreet,
      FieldNames.mailingStreet2,
      FieldNames.mailingCity,
      FieldNames.mailingState,
      FieldNames.mailingZip,
    ]),
  },
  {
    id         : LF_PURCHASE_GET_ADDITIONAL_PROPERTIES,
    title      : 'Do you own any additional properties?',
    questions  : purchaseLFQuestionsByName([
      FieldNames.hasAddlPropertiesYN,
      FieldNames.addlProperties,
    ]),
    showIf: [isHomeowner],
  },
  {
    id       : LF_PURCHASE_GET_BORROWER_INCOME_SOURCE,
    title    : 'What is your income source?',
    questions: purchaseLFQuestionsByName([FieldNames.employmentType]),
  },
  {
    id         : LF_PURCHASE_GET_BORROWER_INCOME_EMPLOYED_OR_SELF,
    title      : 'Tell us about your income and employment',
    questions  : purchaseLFQuestionsByName([
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.lineOfWorkYears,
      FieldNames.lineOfWorkMonths,
      FieldNames.employOwnershipShare,
      FieldNames.employPartyTransaction,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf: [isEmployee, isSelfEmployed],
  },
  {
    id       : LF_PURCHASE_GET_BORROWER_INCOME_RETIRED_OR_OTHER,
    questions: purchaseLFQuestionsByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf: [isRetired, isEmploymentOther],
  },
  {
    id        : LF_PURCHASE_GET_COBORROWER_INCOME_SOURCE,
    showIf    : [hasCoBorrower],
    title     : `What is your co-borrower's income source?`,
    questions : purchaseLFQuestionsByName([FieldNames.coBorrowerEmploymentType]),
  },
  {
    id         : LF_PURCHASE_GET_COBORROWER_INCOME_EMPLOYED_OR_SELF,
    title      : `Tell us about your co-borrower's income and employment`,
    questions  : purchaseLFQuestionsByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployStreet,
      FieldNames.coBorrowerEmployStreet2,
      FieldNames.coBorrowerEmployCity,
      FieldNames.coBorrowerEmployState,
      FieldNames.coBorrowerEmployZip,
      FieldNames.coBorrowerEmployTitle,
      FieldNames.coBorrowerEmployPhone,
      FieldNames.coBorrowerEmployStart,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerLineOfWorkYears,
      FieldNames.coBorrowerLineOfWorkMonths,
      FieldNames.coBorrowerEmployOwnershipShare,
      FieldNames.coBorrowerEmployPartyTransaction,
      FieldNames.coBorrowerEmployOvertime,
      FieldNames.coBorrowerEmployCommission,
      FieldNames.coBorrowerEmployBonus,
      FieldNames.coBorrowerAdditionalIncome,
      FieldNames.coBorrowerEmployPrevName,
      FieldNames.coBorrowerEmployPrevStreet,
      FieldNames.coBorrowerEmployPrevStreet2,
      FieldNames.coBorrowerEmployPrevCity,
      FieldNames.coBorrowerEmployPrevState,
      FieldNames.coBorrowerEmployPrevZip,
      FieldNames.coBorrowerEmployPrevTitle,
      FieldNames.coBorrowerEmployPrevStart,
      FieldNames.coBorrowerEmployPrevEnd,
    ]),
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id       : LF_PURCHASE_GET_COBORROWER_INCOME_RETIRED_OR_OTHER,
    questions: purchaseLFQuestionsByName([
      FieldNames.coBorrowerRetirementIncome,
      FieldNames.coBorrowerOtherIncome,
    ]),
    showIf: [isCoBorrowerRetired, isCoBorrowerEmploymentOther],
  },
  {
    id           : LF_PURCHASE_GET_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getLongFormDeclarationQuestions(BorrowerType.PRIMARY),
    submitIf     : [(values) => !hasCoBorrower(values) && isExcludingSsnQuestions(values)],
    submitAction : FormActions.handleLongFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id           : LF_PURCHASE_GET_COBORROWER_QUESTIONS,
    title        : 'Co-Borrower Questions',
    questions    : getLongFormDeclarationQuestions(BorrowerType.COBORROWER),
    showIf       : [hasCoBorrower],
    submitIf     : [isExcludingSsnQuestions],
    submitAction : FormActions.handleLongFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id            : LF_PURCHASE_GET_BORROWER_SSN,
    questions     : purchaseLFQuestionsByName([FieldNames.ssnQuestionRequirement]),
    showIf        : [(values) => !isExcludingSsnQuestions(values)],
    submitAction  : FormActions.handleLongFormSubmit,
    agreement     : <AuthorizeSsnFullPull />,
  },
  {
    id             : LF_PURCHASE_CONFIRMATION,
    ifNoQuestions  : Confirmation,
    hideProgressBar: true,
  },
];
